"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { MainNav } from "@/components/main-nav";
import { UserNav } from "@/components/user-nav";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { getUser } from "@/lib/api";
import { Menu } from "lucide-react";
import Link from "next/link";
import { FloatingChat } from "@/components/floating-chat";

interface DashboardLayoutProps {
  children: React.ReactNode;
  sidebarNav?: {
    title: string;
    items: {
      href: string;
      label: string;
      icon?: React.ComponentType<{ className?: string }>;
    }[];
  }[];
}

export function DashboardLayout({ children, sidebarNav }: DashboardLayoutProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [user, setUser] = useState<{ username: string; role: string } | null>(null);

  useEffect(() => {
    // 仅在客户端执行
    if (typeof window !== 'undefined') {
      const currentUser = getUser();
      if (!currentUser) {
        router.push("/login");
        return;
      }
      
      // 检查路径和角色是否匹配
      const isTeacherPath = pathname.startsWith('/teacher');
      const isStudentPath = pathname.startsWith('/student');
      const userRole = currentUser.role;
      
      // teacher等同于admin，拥有所有权限
      const isTeacher = userRole === 'teacher' || userRole === 'admin';
      
      // 学生不能访问教师页面
      if (isTeacherPath && userRole === 'student') {
        console.warn('学生尝试访问教师页面，重定向到学生页面');
        router.push('/student');
        return;
      }
      
      // 教师（等同于admin）不能访问学生页面
      if (isStudentPath && isTeacher) {
        console.warn('教师尝试访问学生页面，重定向到教师页面');
        router.push('/teacher');
        return;
      }
      
      // 使用 requestAnimationFrame 避免同步 setState
      requestAnimationFrame(() => {
        setUser(currentUser);
        setMounted(true);
      });
    }
  }, [router, pathname]);

  // 显示加载状态
  if (!mounted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">加载中...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* 顶部导航栏 */}
      <header className="sticky top-0 z-50 w-full border-b bg-white dark:bg-gray-900 shadow-sm">
        <div className="container flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4">
          <div className="flex items-center gap-2 sm:gap-4">
            {sidebarNav && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden h-9 w-9">
                    <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 sm:w-80">
                  <nav className="flex flex-col space-y-4 pt-2">
                    {sidebarNav.map((section) => (
                      <div key={section.title}>
                        <h4 className="mb-2 px-3 text-sm font-semibold text-gray-900 dark:text-white">
                          {section.title}
                        </h4>
                        <div className="space-y-1">
                          {section.items.map((item) => (
                            <Link
                              key={item.href}
                              href={item.href}
                              className={`flex items-center rounded-md px-3 py-2.5 sm:py-2 text-sm font-medium transition-colors touch-manipulation ${
                                pathname === item.href
                                  ? "bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700"
                              }`}
                            >
                              {item.icon && <item.icon className="mr-2 h-4 w-4 sm:h-4 sm:w-4" />}
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </nav>
                </SheetContent>
              </Sheet>
            )}
            <MainNav />
          </div>
          <UserNav />
        </div>
      </header>

      {/* 主内容区 */}
      <div className="flex-1">
        <div className="container mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-start md:grid md:grid-cols-[220px_minmax(0,1fr)] md:gap-4 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-6 py-4 sm:py-6">
            {/* 侧边栏 - 仅桌面端显示 */}
            {sidebarNav && (
              <aside className="hidden md:block w-[220px] lg:w-[240px]">
                <nav className="flex flex-col space-y-4 sticky top-20">
                  {sidebarNav.map((section) => (
                    <div key={section.title}>
                      <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                        {section.title}
                      </h4>
                      <div className="space-y-1">
                        {section.items.map((item) => (
                          <Link
                            key={item.href}
                            href={item.href}
                            className={`flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                              pathname === item.href
                                ? "bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                            }`}
                          >
                            {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </nav>
              </aside>
            )}

            {/* 主内容 */}
            <main className="flex-1 min-w-0 w-full">
              {children}
            </main>
          </div>
        </div>
      </div>

      {/* 浮动聊天室 */}
      <FloatingChat defaultCollapsed={true} position="bottom-right" />
    </div>
  );
}
